import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  useCreateCustomMutation,
  useGenericRequestMutation,
} from "@/store/features/custom/customAPI";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  Clock,
  Users,
  Loader2,
  Globe,
  Camera,
  Shuffle,
  Edit3,
  Ticket,
  Sparkles,
  DollarSign,
  Calendar,
  Video,
  Image,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import MDEditor from "@uiw/react-md-editor";
import { useEnhanceEventDescriptionMutation } from "@/store/features/ai-events/aiEventsAPI";
import { useGetEventCategoriesQuery } from "@/store/features/events/eventsAPI";

const mediaUrl = import.meta.env.VITE_MEDIA_URL;

const eventGenerationSchema = z
  .object({
    title: z.string().min(1, "Event name is required").max(300),
    description: z.string().optional(),
    category: z.string().min(1, "Category is required"),
    event_type: z.string().min(1, "Event type is required"),
    start_date: z.string().min(1, "Start date is required"),
    start_time: z.string().min(1, "Start time is required"),
    end_date: z.string().min(1, "End date is required"),
    end_time: z.string().min(1, "End time is required"),
    timezone: z.string().default("Asia/Kolkata"),
    location: z
      .array(
        z.object({
          name: z.string().min(1, "Location is required"),
          city: z.string().default(""),
          address: z.string().optional(),
          latitude: z.coerce.number(),
          longitude: z.coerce.number(),
          state: z.string().optional(),
          country: z.string().default("India"),
          postal_code: z.string().optional(),
        })
      )
      .min(1),
    capacity: z.coerce.number().int().min(0).nullable(),
    base_price: z.coerce.number().min(0).default(0),
    currency: z.enum(["INR", "USD"]).default("INR"),
    visibility: z.enum(["public", "private", "unlisted", "restricted"]).default("public"),
    require_approval: z.boolean().default(false),
    waitlist_enabled: z.boolean().default(false),
    ai_template: z.string().nullable().optional(),
    media: z
      .object({
        thumbnail: z
          .object({
            url: z.string().optional(),
            width: z.number().optional(),
            height: z.number().optional(),
            id: z.number().optional(),
          })
          .optional(),
        featured_image: z
          .object({
            url: z.string().optional(),
            width: z.number().optional(),
            height: z.number().optional(),
            id: z.number().optional(),
          })
          .optional(),
        banner_image: z
          .object({
            url: z.string().optional(),
            width: z.number().optional(),
            height: z.number().optional(),
            id: z.number().optional(),
          })
          .optional(),
        gallery: z
          .array(
            z.object({
              url: z.string().optional(),
              width: z.number().optional(),
              height: z.number().optional(),
              id: z.number().optional(),
            })
          )
          .optional(),
        promotional_video: z
          .object({
            url: z.string().optional(),
            id: z.number().optional(),
          })
          .optional(),
      })
      .optional()
      .default({}),
  })
  .refine(
    (data) =>
      data.media?.thumbnail?.url ||
      data.media?.featured_image?.url ||
      data.media?.banner_image?.url,
    {
      message: "At least one main image (thumbnail, featured, or banner) is required",
      path: ["media"],
    }
  )
  .superRefine(({ start_date, end_date, start_time, end_time }, ctx) => {
    if (end_date < start_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date",
        path: ["end_date"],
      });
    }
    if (end_date === start_date && end_time <= start_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End time must be after start time",
        path: ["end_time"],
      });
    }
  });

type EventFormData = z.infer<typeof eventGenerationSchema>;

const EVENTTYPE = [
  { id: 1, name: "Conference", value: "conference" },
  { id: 2, name: "Workshop", value: "workshop" },
  { id: 3, name: "Seminar", value: "seminar" },
  { id: 4, name: "Webinar", value: "webinar" },
  { id: 5, name: "Networking", value: "networking" },
  { id: 6, name: "Concert", value: "concert" },
  { id: 7, name: "Festival", value: "festival" },
  { id: 8, name: "Sports", value: "sports" },
  { id: 9, name: "Exhibition", value: "exhibition" },
  { id: 10, name: "Training", value: "training" },
  { id: 11, name: "Hackathon", value: "hackathon" },
  { id: 12, name: "Masterclass", value: "masterclass" },
];

export default function GenerateEventPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const temp_id = queryParams.get('temp_id');
  console.log(temp_id,"temp_id");
  const [createCustom, { isLoading: isCreating }] = useCreateCustomMutation();
  const [genericRequest] = useGenericRequestMutation();
  const { data: categoriesData } = useGetEventCategoriesQuery();
  const [enhanceDescription, { isLoading: isEnhancing }] = useEnhanceEventDescriptionMutation();

  const [eventsCategory, setEventsCategory] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);
  const [tempCapacity, setTempCapacity] = useState<number | null>(null);
  const [tempWaitlist, setTempWaitlist] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: string]: File }>({});
  const [mediaPreviews, setMediaPreviews] = useState<{
    thumbnail?: string;
    featured_image?: string;
    banner_image?: string;
    gallery: string[];
    promotional_video?: string;
  }>({
    thumbnail: "",
    featured_image: "",
    banner_image: "",
    gallery: [],
    promotional_video: "",
  });
  const [imageDimensions, setImageDimensions] = useState<{
    thumbnail: { width: number; height: number };
    featured_image: { width: number; height: number };
    banner_image: { width: number; height: number };
    gallery: Array<{ width: number; height: number }>;
  }>({
    thumbnail: { width: 0, height: 0 },
    featured_image: { width: 0, height: 0 },
    banner_image: { width: 0, height: 0 },
    gallery: [],
  });

  const form = useForm<EventFormData>({
    resolver: zodResolver(eventGenerationSchema),
    defaultValues: {
      title: "",
      event_type: "",
      description: "",
      category: "",
      start_date: "",
      start_time: "15:00",
      end_date: "",
      end_time: "16:00",
      timezone: "Asia/Kolkata",
      location: [{ name: "", city: "", address: "", latitude: 0, longitude: 0, state: "", country: "India", postal_code: "" }],
      capacity: null,
      base_price: 0,
      currency: "INR",
      visibility: "public",
      require_approval: false,
      waitlist_enabled: false,
      ai_template: temp_id ? temp_id : null,
      media: {
        thumbnail: { url: "", width: 0, height: 0, id: 0 },
        featured_image: { url: "", width: 0, height: 0, id: 0 },
        banner_image: { url: "", width: 0, height: 0, id: 0 },
        gallery: [],
        promotional_video: { url: "", id: 0 },
      },
    },
    mode: "onChange",
  });

  const watchedValues = form.watch();

  useEffect(() => {
    if (categoriesData?.data) {
      setEventsCategory(categoriesData.data);
    }
  }, [categoriesData]);

  // Strapi Upload Function
  const uploadToStrapi = async (file: File) => {
    const formData = new FormData();
    formData.append('files', file);

    try {
      const result = await genericRequest({
        url: '/upload',
        method: 'POST',
        data: formData,
      }).unwrap();

      const mediadata = { url: result[0]?.url, id: result[0]?.id }
      return mediadata || null;
    } catch (error) {
      toast.error('Upload failed');
      return null;
    }
  };

  // Image validation
  const validateImageRatio = useCallback((file: File, type: 'thumbnail' | 'featured_image' | 'banner_image'): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = document.createElement('img');
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const { width, height } = img;
        URL.revokeObjectURL(objectUrl);

        const ratios = {
          thumbnail: { ratio: 9 / 16, ideal: '9:16 portrait', recommended: '1080x1920' },
          featured_image: { ratio: 16 / 9, ideal: '16:9 landscape', recommended: '1920x1080' },
          banner_image: { ratio: 16 / 9, ideal: '16:9 landscape', recommended: '1920x1080' },
        };

        const target = ratios[type];
        const actualRatio = width / height;
        const tolerance = 0.05;

        if (Math.abs(actualRatio - target.ratio) > tolerance) {
          toast.error(
            `${type.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase()}: Actual ${width}x${height} (${actualRatio.toFixed(2)}) Expected ${target.ideal} (${target.recommended})`
          );
          resolve(false);
        } else {
          setImageDimensions(prev => ({
            ...prev,
            [type]: { width, height }
          }));
          toast.success(`${type.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase()} validated ${width}x${height}`);
          resolve(true);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        toast.error('Failed to process image');
        resolve(false);
      };

      img.src = objectUrl;
    });
  }, []);

  // Complete handleFileUpload with Strapi upload
  const handleFileUpload = async (field: 'thumbnail' | 'featured_image' | 'banner_image' | 'gallery', file: File) => {
    if (!file.type.startsWith('image')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error('File size must be less than 8MB');
      return;
    }

    // Validate ratio for non-gallery images
    if (field !== 'gallery') {
      const isValidRatio = await validateImageRatio(file, field);
      if (!isValidRatio) return;
    }

    const result = await uploadToStrapi(file);
    const uploadedUrl = result?.url;
    const uploadedId = result?.id;

    if (!uploadedUrl) return;

    const previewUrl = URL.createObjectURL(file);

    if (field === 'gallery') {
      setMediaPreviews(prev => ({
        ...prev,
        gallery: [...prev.gallery, previewUrl]
      }));

      const currentGallery = watchedValues.media?.gallery || [];
      form.setValue('media.gallery', [
        ...currentGallery,
        { url: uploadedUrl, width: 0, height: 0, id: uploadedId }
      ]);
    } else {
      setMediaPreviews(prev => ({ ...prev, [field]: previewUrl }));
      form.setValue(`media.${field}`, {
        url: uploadedUrl,
        width: 0,
        height: 0,
        id: uploadedId
      });
    }

    toast.success(`${field.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase()} uploaded successfully!`);
  };

  const handleAIGenerate = async () => {
    const title = form.getValues('title');
    const category = form.getValues('category');
    const eventType = form.getValues('event_type');

    if (!title || !category || !eventType) {
      toast.error('Please provide title, category, and event type');
      return;
    }

    try {
      const result = await enhanceDescription({
        description: title,
        industry: category,
        eventType: eventType,
        tone: 'professional',
        model: 'gemini',
        event_id: '',
        current_description: '',
      } as any).unwrap();

      if (result?.data?.enhanced_description) {
        form.setValue('description', result.data.enhanced_description, { shouldValidate: true });
        toast.success('AI description generated!');
      }
    } catch (err) {
      toast.error('Failed to generate description');
    }
  };

  const handleOpenCapacityDialog = () => {
    setTempCapacity(form.getValues('capacity'));
    setTempWaitlist(form.getValues('waitlist_enabled'));
    setShowCapacityDialog(true);
  };

  const handleSaveCapacity = () => {
    form.setValue('capacity', tempCapacity);
    form.setValue('waitlist_enabled', tempWaitlist);
    setShowCapacityDialog(false);
  };

  const handleRemoveCapacity = () => {
    form.setValue('capacity', null);
    form.setValue('waitlist_enabled', false);
    setShowCapacityDialog(false);
  };

  // Convert HH:mm to HH:mm:ss.SSS
  const formatTimeForStrapi = (time: string) => {
    if (!time) return null;
    if (time.match(/^\d{2}:\d{2}:\d{2}\.\d{3}$/)) {
      return time;
    }
    if (time.match(/^\d{2}:\d{2}$/)) {
      return `${time}:00.000`;
    }
    if (time.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return `${time}.000`;
    }
    return time;
  };

  const prepareMediaData = (data: any) => {
    const cleaned = { ...data };
    Object.keys(cleaned).forEach(key => {
      if (!cleaned[key] || cleaned[key] === 0) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  const onSubmit = async (data: EventFormData) => {
    try {
      const mediaData = {
        thumbnail: data.media.thumbnail?.id,
        featured_image: data.media.featured_image?.id,
        banner_image: data.media.banner_image?.id,
        promotional_video: data.media.promotional_video?.id,
        gallery: data.media.gallery?.map(item => item.id).filter(Boolean) || [],
      };

      const cleanedData = prepareMediaData(mediaData);

      const eventData = {
        ...data,
        slug: data.title.toLowerCase().replace(/[^a-z0-9]/g, '-'),
        record_status: 'draft' as const,
        end_time: formatTimeForStrapi(data.end_time),
        start_time: formatTimeForStrapi(data.start_time),
        media: cleanedData,
        ai_template: temp_id ? temp_id : null,
        category: data.category,
      };

      let collectionName = 'event';
      let collectionKey = 'organizer';
      const path = `${collectionName}/${collectionKey}`;
      const postdata = { data: eventData, path };

      const result = await createCustom(postdata).unwrap();

      toast.success('Event created successfully!');
      setTimeout(() => navigate(`/event/${result.data.documentId}`), 1500);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || 'Failed to create event');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-theme-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-theme-secondary/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-theme-primary via-theme-secondary to-theme-primary bg-clip-text text-transparent mb-2">
            Create Your Event
          </h1>
          <p className="text-muted-foreground">Let AI help you create an amazing event website in minutes</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* LEFT SIDE - Complete Media Gallery */}
              <Card className="bg-card/60 backdrop-blur-xl border-border/40 shadow-2xl">
                <CardContent className="p-6 space-y-4">
                  {/* Main Event Poster Preview */}
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-theme-primary/20 to-theme-secondary/20 group">
                    {mediaPreviews.featured_image ? (
                      <img src={mediaPreviews.featured_image} alt="Featured" className="w-full h-full object-cover" />
                    ) : mediaPreviews.thumbnail ? (
                      <img src={mediaPreviews.thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <Camera className="w-16 h-16 text-muted-foreground/50 mb-2" />
                        <p className="text-sm text-muted-foreground">Event Poster</p>
                        <p className="text-xs text-muted-foreground/70">16:9 Featured or 9:16 Thumbnail</p>
                      </div>
                    )}

                    {/* Upload Overlay */}
                    <div 
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                      onClick={() => document.getElementById('featured_image')?.click()}
                    >
                      <Camera className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {/* Individual Image Uploads with Ratio Validation */}
                  <div className="grid grid-cols-3 gap-3">
                    {(['thumbnail', 'featured_image', 'banner_image'] as const).map((type) => {
                      const previewUrl = mediaPreviews[type];
                      const dimensions = imageDimensions[type];

                      return (
                        <div key={type} className="space-y-1">
                          <Label className="text-xs capitalize">
                            {type.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase()}
                          </Label>
                          <p className="text-[10px] text-muted-foreground">
                            {type === 'thumbnail' ? '9:16 Portrait' : '16:9 Landscape'}
                          </p>
                          <Input
                            id={type}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload(type, file);
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full h-20 relative overflow-hidden"
                            onClick={() => document.getElementById(type)?.click()}
                          >
                            {previewUrl ? (
                              <img src={previewUrl} alt={type} className="absolute inset-0 w-full h-full object-cover" />
                            ) : (
                              <Camera className="w-6 h-6 text-muted-foreground" />
                            )}
                          </Button>
                          {previewUrl && (
                            <p className="text-[10px] text-green-500">
                              {dimensions.width}x{dimensions.height}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Gallery Upload */}
                  <div className="space-y-2">
                    <Label>Gallery Photos (16:9 recommended)</Label>
                    <Input
                      id="gallery"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        Array.from(e.target.files || []).forEach((file) => {
                          handleFileUpload('gallery', file);
                        });
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => document.getElementById('gallery')?.click()}
                    >
                      <Image className="w-4 h-4 mr-2" />
                      Upload Gallery Images
                    </Button>

                    {/* Gallery Preview */}
                    {mediaPreviews.gallery.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {mediaPreviews.gallery.slice(-6).map((preview, idx) => (
                          <img key={idx} src={preview} alt={`Gallery ${idx}`} className="w-full aspect-video object-cover rounded" />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Promotional Video */}
                  <div className="space-y-2">
                    <Label>Promotional Video</Label>
                    <Input
                      placeholder="YouTube video URL"
                      value={watchedValues.media?.promotional_video?.url || ''}
                      onChange={(e) => form.setValue('media.promotional_video.url', e.target.value)}
                      className="h-12"
                    />
                    {mediaPreviews.promotional_video && (
                      <p className="text-xs text-green-500 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        YouTube video ready
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* RIGHT SIDE - Form Fields */}
              <div className="space-y-6">
                <Card className="bg-card/60 backdrop-blur-xl border-border/40 shadow-2xl">
                  <CardContent className="p-6 space-y-4">
                    {/* Top Controls */}
                    <div className="flex gap-2">
                      <FormField
                        control={form.control}
                        name="visibility"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-12 bg-background/60">
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="public">Public</SelectItem>
                                <SelectItem value="private">Private</SelectItem>
                                <SelectItem value="unlisted">Unlisted</SelectItem>
                                <SelectItem value="restricted">Restricted</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="event_type"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-12 bg-background/60">
                                  <SelectValue placeholder="Event Type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {EVENTTYPE.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.value}>
                                    {cat.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="category"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-12 bg-background/60">
                                  <SelectValue placeholder="Category" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {eventsCategory.map((cat: any) => (
                                  <SelectItem key={cat.id} value={cat.documentId}>
                                    {cat.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Title */}
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Event Title"
                              className="h-16 text-2xl font-bold bg-background/60"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Date & Time */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Start</Label>
                        <FormField
                          control={form.control}
                          name="start_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input type="date" {...field} className="h-12 bg-background/60" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="start_time"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input type="time" {...field} className="h-12 bg-background/60" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">End</Label>
                        <FormField
                          control={form.control}
                          name="end_date"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input type="date" {...field} className="h-12 bg-background/60" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="end_time"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input type="time" {...field} className="h-12 bg-background/60" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-accent/20 p-3 rounded-lg">
                      <Globe className="w-4 h-4" />
                      <span>{watchedValues.timezone}</span>
                      <span className="ml-auto">India Standard Time</span>
                    </div>

                     {/* Location - YOUR EXACT CODE */}
                                      <FormField
                                        control={form.control}
                                        name="location.0.address"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormControl>
                                              <div className="flex items-center gap-3 bg-card/40 backdrop-blur-xl rounded-lg p-4 border border-border/40">
                                                <MapPin className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                                <Input
                                                  {...field}
                                                  disabled={isCreating || isEnhancing}
                                                  placeholder="Add Event Address"
                                                  className="bg-transparent border-0 text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 px-0"
                                                />
                                              </div>
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                    
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <FormField
                                          control={form.control}
                                          name="location.0.name"
                                          render={({ field }) => (
                                            <div className="flex items-center gap-3 bg-card/40 backdrop-blur-xl rounded-lg p-4 border border-border/40">
                                              <MapPin className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                              <Input
                                                {...field}
                                                disabled={isCreating || isEnhancing}
                                                placeholder="Venue Name"
                                                className="bg-transparent border-0 text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 px-0"
                                              />
                                            </div>
                                          )}
                                        />
                                        <FormField
                                          control={form.control}
                                          name="location.0.city"
                                          render={({ field }) => (
                                            <div className="flex items-center gap-3 bg-card/40 backdrop-blur-xl rounded-lg p-4 border border-border/40">
                                              <MapPin className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                              <Input
                                                {...field}
                                                disabled={isCreating || isEnhancing}
                                                placeholder="City"
                                                className="bg-transparent border-0 text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 px-0"
                                              />
                                            </div>
                                          )}
                                        />
                                      </div>

                    

                    {/* Description with AI */}
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between mb-2">
                            <FormLabel>Description *</FormLabel>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleAIGenerate}
                              disabled={isEnhancing}
                            >
                              {isEnhancing ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  Generate with AI
                                </>
                              )}
                            </Button>
                          </div>
                          <FormControl>
                            <MDEditor
                              value={field.value}
                              onChange={(value) => field.onChange(value ?? '')}
                              height={300}
                              className="bg-transparent"
                              data-color-mode="dark"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Event Options */}
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold">Event Options</Label>
 <FormField
                      control={form.control}
                      name="base_price"
                      render={({ field }) => (
                        <div className="flex items-center gap-3 bg-card/40 backdrop-blur-xl rounded-lg p-4 border border-border/40">
                          <DollarSign className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          <Input
                            {...field}
                            type="number"
                            disabled={isCreating || isEnhancing}
                            placeholder="Ticket Price (Enter 0 for free events)"
                            className="bg-transparent border-0 text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 px-0"
                          />
                        </div>
                      )}
                    />
                      <FormField
                        control={form.control}
                        name="require_approval"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between bg-card/40 backdrop-blur-xl rounded-lg p-4 border border-border/40">
                            <div>
                              <FormLabel>Require Approval</FormLabel>
                            </div>
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <div
                        onClick={() => setShowPaymentDialog(true)}
                        className="w-full flex items-center justify-between bg-card/40 backdrop-blur-xl rounded-lg p-4 border border-border/40 hover:bg-accent/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <Ticket className="w-5 h-5" />
                          <span>Tickets</span>
                        </div>
                        <span className="font-semibold">
                          {watchedValues.base_price === 0 ? 'Free' : `₹${watchedValues.base_price}`}
                        </span>
                      </div>

                      <div
                        onClick={handleOpenCapacityDialog}
                        className="w-full flex items-center justify-between bg-card/40 backdrop-blur-xl rounded-lg p-4 border border-border/40 hover:bg-accent/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5" />
                          <span>Capacity</span>
                        </div>
                        <span className="font-semibold">
                          {watchedValues.capacity ? watchedValues.capacity : 'Unlimited'}
                        </span>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isCreating}
                      className="w-full h-14 text-lg bg-gradient-to-r from-theme-primary to-theme-secondary hover:opacity-90 transition-all"
                    >
                      {isCreating ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Event'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </Form>

        {/* Capacity Dialog */}
        <Dialog open={showCapacityDialog} onOpenChange={setShowCapacityDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Capacity & Waitlist</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Capacity</Label>
                <Input
                  type="number"
                  value={tempCapacity || ''}
                  onChange={(e) => setTempCapacity(e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Enter capacity"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Enable Waitlist</Label>
                <Switch checked={tempWaitlist} onCheckedChange={setTempWaitlist} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleRemoveCapacity}>
                Unlimited
              </Button>
              <Button onClick={handleSaveCapacity}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
